import { TextField } from '@solvhealth/jigsaw-v1';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { formShape } from 'rc-form';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { selectors as insurancePlansSelectors } from '~/ducks/insuranceCarrierPlans';
import { useSolvSelector } from '~/reducers';
import { PaperworkFieldName } from '../../paperworkConstants';
import { getStandardFormFieldData } from '../../util';
import { SolvAutocomplete } from './SolvAutocomplete';
import {
  InsuranceCarrierOption,
  InsurancePlanOption,
  isMetaOption,
  isMetaOther,
  isMetaUnknown,
  otherMetaOption,
  renderInsuranceCarrierOptionLabel,
  renderInsurancePlanOptionLabel,
  unknownMetaOption,
  useAcceptedInsurancePlans,
  useInitialCarrierOption,
} from './util';

const StyledInsuranceCardIcon = styled.img.attrs({
  src: '/images/paperwork/insuranceCardIcon.svg',
}).withConfig({ componentId: 'sc-16bth64-0' })`
  margin-right: 16px;
`;

const filterInsuranceCarrierOptions = (
  options: InsuranceCarrierOption[],
  { inputValue }: { inputValue: string }
) =>
  options.filter(
    (option) =>
      isMetaOption(option) ||
      option.display_name.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
  );

const filterInsurancePlanOptions = (
  options: InsurancePlanOption[],
  { inputValue }: { inputValue: string }
) =>
  options.filter(
    (option) =>
      isMetaOption(option) ||
      option.insurance_plan_display_name.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
  );

export const InsuranceCarrierAutocomplete = ({
  form,
  paperwork,
  initialCarrierOptionLabel,
}: {
  form: formShape;
  paperwork: any;
  initialCarrierOptionLabel?: string | null;
}) => {
  const primaryInsuranceCarrierField = getStandardFormFieldData(
    PaperworkFieldName.PRIMARY_INSURANCE_COMPANY,
    paperwork
  );

  const primaryInsurancePlanField = getStandardFormFieldData(
    PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME,
    paperwork
  );

  const acceptedInsuranceCarriers = useSolvSelector(
    insurancePlansSelectors.getInsurersThatHavePlansSelector
  );

  // If there is some pre-filled textual value for this field, we should lookup the corresponding
  // option and use that as the initial value.
  const initialCarrierOption = useInitialCarrierOption(
    acceptedInsuranceCarriers,
    initialCarrierOptionLabel
  );

  // Do this up here, so that when we query antd for selectedInsuranceCarrier,
  // it knows about the "initialValue".
  const insuranceAutocompleteAntdFormProps: unknown = form.getFieldProps(
    primaryInsuranceCarrierField.field_name,
    {
      initialValue: initialCarrierOption,
      rules: [
        {
          required: true,
          message: 'Please select from one of the available options',
        },
      ],
    }
  );

  const selectedInsuranceCarrier =
    form.getFieldValue(
      primaryInsuranceCarrierField?.field_name ?? PaperworkFieldName.PRIMARY_INSURANCE_COMPANY
    ) ?? null;

  const acceptedInsurancePlans = useAcceptedInsurancePlans(selectedInsuranceCarrier);

  const insuranceCarrierOptions: InsuranceCarrierOption[] = useMemo(
    () => [...acceptedInsuranceCarriers, otherMetaOption, unknownMetaOption],
    [acceptedInsuranceCarriers]
  );

  const insurancePlanOptions: InsurancePlanOption[] = useMemo(
    () => [...acceptedInsurancePlans, otherMetaOption, unknownMetaOption],
    [acceptedInsurancePlans]
  );

  const planAutocompleteDisabled =
    !selectedInsuranceCarrier ||
    selectedInsuranceCarrier === otherMetaOption ||
    selectedInsuranceCarrier === unknownMetaOption;

  if (!primaryInsuranceCarrierField) {
    return null;
  }

  return (
    <>
      <SolvAutocomplete
        // @ts-expect-error
        {...insuranceAutocompleteAntdFormProps}
        autoHighlight
        filterOptions={filterInsuranceCarrierOptions}
        getOptionLabel={renderInsuranceCarrierOptionLabel}
        onBlur={() => form.validateFields([primaryInsuranceCarrierField.field_name])}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string' || Array.isArray(newValue)) {
            return;
          }
          const currentValue = form.getFieldValue(primaryInsuranceCarrierField.field_name);
          const changedToOther = isMetaOther(newValue);
          const changedToUnknown = isMetaUnknown(newValue);
          form.setFields({
            [primaryInsuranceCarrierField.field_name]: {
              value: newValue,
              touched: true,
            },
            ...(!!primaryInsurancePlanField && {
              ...(newValue !== currentValue && {
                [primaryInsurancePlanField.field_name]: {
                  value: null,
                  touched: false,
                },
              }),
              ...(changedToOther && {
                [primaryInsurancePlanField.field_name]: {
                  value: otherMetaOption,
                  touched: true,
                },
              }),
              ...(changedToUnknown && {
                [primaryInsurancePlanField.field_name]: {
                  value: unknownMetaOption,
                  touched: true,
                },
              }),
            }),
          });
        }}
        options={insuranceCarrierOptions}
        renderTextField={(textFieldProps) => (
          <TextField
            {...textFieldProps}
            dense
            endIcon={<StyledInsuranceCardIcon />}
            error={form.getFieldError(primaryInsuranceCarrierField.field_name)?.[0] ?? ''}
            title={primaryInsuranceCarrierField.field_display_name}
          />
        )}
      />
      {!!primaryInsurancePlanField && (
        <SolvAutocomplete
          // @ts-expect-error
          {...(form.getFieldProps(primaryInsurancePlanField.field_name, {
            initialValue: null,
            rules: [
              {
                required: !planAutocompleteDisabled,
                message: 'Please select from one of the available options',
              },
            ],
          }) as unknown)}
          autoHighlight
          disabled={planAutocompleteDisabled}
          filterOptions={filterInsurancePlanOptions}
          getOptionLabel={renderInsurancePlanOptionLabel}
          onBlur={() => form.validateFields([primaryInsurancePlanField.field_name])}
          onChange={(event, newValue) =>
            form.setFields({
              [primaryInsurancePlanField.field_name]: {
                value: newValue,
                touched: true,
              },
            })
          }
          options={insurancePlanOptions}
          renderTextField={(textFieldProps) => (
            <TextField
              {...textFieldProps}
              dense
              endIcon={<StyledInsuranceCardIcon />}
              error={form.getFieldError(primaryInsurancePlanField.field_name)?.[0] ?? ''}
              title={primaryInsurancePlanField.field_display_name}
            />
          )}
        />
      )}
    </>
  );
};
